import type { APIClient, Whitelist, YouTubeSearchResponse, YouTubeAlbumResponse, Artist } from '@/types';
import { ZemerError } from '@/types';

const API_BASE_URL = 'https://zemer-music-api.abesternheim.workers.dev/api';
const FIRESTORE_PROJECT_ID = 'zemer-7f5f1';
const FIRESTORE_WHITELIST_URL = `https://firestore.googleapis.com/v1/projects/${FIRESTORE_PROJECT_ID}/databases/(default)/documents/artistsWhitelist`;

// localStorage keys and cache duration
const WHITELIST_STORAGE_KEY = 'zemer_artists_whitelist_v2'; // v2 to force refresh with full data
const WHITELIST_TIMESTAMP_KEY = 'zemer_artists_whitelist_timestamp_v2';
const WHITELIST_CACHE_DURATION = 1000 * 60 * 60 * 24; // 24 hours
const ARTIST_THUMBNAILS_KEY = 'zemer_artist_thumbnails';

// Firestore REST API response types
interface FirestoreValue {
  stringValue?: string;
  integerValue?: string;
  booleanValue?: boolean;
  arrayValue?: { values?: FirestoreValue[] };
  mapValue?: { fields?: Record<string, FirestoreValue> };
}

interface FirestoreDocument {
  name: string;
  fields?: Record<string, FirestoreValue>;
  createTime?: string;
  updateTime?: string;
}

interface FirestoreListResponse {
  documents?: FirestoreDocument[];
  nextPageToken?: string;
}

class APIClientImpl implements APIClient {
  private baseUrl: string;
  private requestCache: Map<string, { data: unknown; timestamp: number }>;
  private cacheDuration: number = 1000 * 60 * 5; // 5 minutes
  private artistThumbnails: Map<string, string>;
  private thumbnailFetchQueue: Set<string>;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.requestCache = new Map();
    this.artistThumbnails = new Map();
    this.thumbnailFetchQueue = new Set();
    this.loadThumbnailCache();
  }

  private loadThumbnailCache(): void {
    try {
      const stored = localStorage.getItem(ARTIST_THUMBNAILS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as Record<string, string>;
        Object.entries(parsed).forEach(([key, value]) => {
          this.artistThumbnails.set(key, value);
        });
        console.log(`🖼️ Loaded ${this.artistThumbnails.size} cached artist thumbnails`);
      }
    } catch (error) {
      console.warn('Failed to load thumbnail cache:', error);
    }
  }

  private saveThumbnailCache(): void {
    try {
      const obj: Record<string, string> = {};
      this.artistThumbnails.forEach((value, key) => {
        obj[key] = value;
      });
      localStorage.setItem(ARTIST_THUMBNAILS_KEY, JSON.stringify(obj));
    } catch (error) {
      console.warn('Failed to save thumbnail cache:', error);
    }
  }

  getArtistThumbnailFromCache(artistId: string): string | undefined {
    return this.artistThumbnails.get(artistId);
  }

  async fetchArtistThumbnail(artistId: string, artistName: string): Promise<string | null> {
    // Check cache first
    const cached = this.artistThumbnails.get(artistId);
    if (cached) return cached;

    // Don't fetch if already in queue
    if (this.thumbnailFetchQueue.has(artistId)) return null;
    this.thumbnailFetchQueue.add(artistId);

    try {
      // Browse the artist to get their thumbnail
      const response = await fetch(`${this.baseUrl}/browse/${encodeURIComponent(artistId)}`);
      if (!response.ok) {
        this.thumbnailFetchQueue.delete(artistId);
        return null;
      }

      const data = await response.json();

      // The worker wraps response in { browseId, results }
      const browseData = data.results || data;

      // Extract thumbnail from browse response
      let thumbnail: string | null = null;

      // Try to find thumbnail in various places in the response
      if (browseData.header?.musicImmersiveHeaderRenderer?.thumbnail?.musicThumbnailRenderer?.thumbnail?.thumbnails) {
        const thumbs = browseData.header.musicImmersiveHeaderRenderer.thumbnail.musicThumbnailRenderer.thumbnail.thumbnails;
        thumbnail = thumbs[thumbs.length - 1]?.url || thumbs[0]?.url;
      } else if (browseData.header?.musicDetailHeaderRenderer?.thumbnail?.croppedSquareThumbnailRenderer?.thumbnail?.thumbnails) {
        const thumbs = browseData.header.musicDetailHeaderRenderer.thumbnail.croppedSquareThumbnailRenderer.thumbnail.thumbnails;
        thumbnail = thumbs[thumbs.length - 1]?.url || thumbs[0]?.url;
      } else if (browseData.header?.musicVisualHeaderRenderer?.foregroundThumbnail?.musicThumbnailRenderer?.thumbnail?.thumbnails) {
        // Another common format for artist pages
        const thumbs = browseData.header.musicVisualHeaderRenderer.foregroundThumbnail.musicThumbnailRenderer.thumbnail.thumbnails;
        thumbnail = thumbs[thumbs.length - 1]?.url || thumbs[0]?.url;
      } else if (browseData.thumbnail) {
        thumbnail = browseData.thumbnail;
      }

      if (thumbnail) {
        this.artistThumbnails.set(artistId, thumbnail);
        this.saveThumbnailCache();
        console.log(`🖼️ Fetched thumbnail for ${artistName}`);
      }

      this.thumbnailFetchQueue.delete(artistId);
      return thumbnail;
    } catch (error) {
      console.warn(`Failed to fetch thumbnail for ${artistName}:`, error);
      this.thumbnailFetchQueue.delete(artistId);
      return null;
    }
  }

  private parseFirestoreArtist(doc: FirestoreDocument): Artist | null {
    const fields = doc.fields;
    if (!fields) return null;

    const name = fields.name?.stringValue;
    if (!name) return null;

    return {
      name,
      id: fields.id?.stringValue,
      link: fields.link?.stringValue,
      thumbnail: fields.thumbnail?.stringValue || fields.image?.stringValue,
    };
  }

  private async fetchWithCache<T>(
    url: string,
    options?: RequestInit,
    cacheKey?: string
  ): Promise<T> {
    // Check cache first
    if (cacheKey) {
      const cached = this.requestCache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
        return cached.data as T;
      }
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
      });

      if (!response.ok) {
        throw new ZemerError(
          `HTTP ${response.status}: ${response.statusText}`,
          'HTTP_ERROR',
          { status: response.status, url }
        );
      }

      const data = await response.json();

      // Cache the response
      if (cacheKey) {
        this.requestCache.set(cacheKey, { data, timestamp: Date.now() });
      }

      return data as T;
    } catch (error) {
      if (error instanceof ZemerError) {
        throw error;
      }
      throw new ZemerError(
        'Network request failed',
        'NETWORK_ERROR',
        { originalError: error, url }
      );
    }
  }

  private getLocalStorageWhitelist(): Whitelist | null {
    try {
      const stored = localStorage.getItem(WHITELIST_STORAGE_KEY);
      const timestamp = localStorage.getItem(WHITELIST_TIMESTAMP_KEY);

      if (stored && timestamp) {
        const age = Date.now() - parseInt(timestamp, 10);
        if (age < WHITELIST_CACHE_DURATION) {
          const whitelist = JSON.parse(stored) as Whitelist;
          console.log(`📋 Loaded ${whitelist.artists.length} artists from localStorage cache`);
          return whitelist;
        }
      }
    } catch (error) {
      console.warn('Failed to read whitelist from localStorage:', error);
    }
    return null;
  }

  private saveWhitelistToLocalStorage(whitelist: Whitelist): void {
    try {
      localStorage.setItem(WHITELIST_STORAGE_KEY, JSON.stringify(whitelist));
      localStorage.setItem(WHITELIST_TIMESTAMP_KEY, Date.now().toString());
      console.log(`💾 Saved ${whitelist.artists.length} artists to localStorage`);
    } catch (error) {
      console.warn('Failed to save whitelist to localStorage:', error);
    }
  }

  async getWhitelist(): Promise<Whitelist> {
    // Check memory cache first
    const cached = this.requestCache.get('whitelist');
    if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
      return cached.data as Whitelist;
    }

    // Check localStorage cache
    const localCached = this.getLocalStorageWhitelist();
    if (localCached) {
      // Store in memory cache too
      this.requestCache.set('whitelist', { data: localCached, timestamp: Date.now() });
      return localCached;
    }

    // Fetch all pages from Firestore
    try {
      const artists: Artist[] = [];
      let pageToken: string | undefined;
      let pageCount = 0;

      do {
        // Build URL with pagination
        let url = `${FIRESTORE_WHITELIST_URL}?pageSize=300`;
        if (pageToken) {
          url += `&pageToken=${encodeURIComponent(pageToken)}`;
        }

        const response = await fetch(url);

        if (!response.ok) {
          throw new ZemerError(
            `HTTP ${response.status}: ${response.statusText}`,
            'HTTP_ERROR',
            { status: response.status, url }
          );
        }

        const data: FirestoreListResponse = await response.json();
        pageCount++;

        // Parse Firestore documents into Artist objects
        if (data.documents) {
          for (const doc of data.documents) {
            const artist = this.parseFirestoreArtist(doc);
            if (artist) {
              artists.push(artist);
            }
          }
        }

        // Get next page token
        pageToken = data.nextPageToken;
        console.log(`📋 Page ${pageCount}: loaded ${data.documents?.length || 0} artists (total: ${artists.length})`);

      } while (pageToken);

      // Sort artists alphabetically
      artists.sort((a, b) => a.name.localeCompare(b.name));

      const whitelist: Whitelist = { artists };

      // Cache in memory
      this.requestCache.set('whitelist', { data: whitelist, timestamp: Date.now() });

      // Save to localStorage
      this.saveWhitelistToLocalStorage(whitelist);

      console.log(`📋 Loaded ${artists.length} artists from Firestore (${pageCount} pages)`);
      return whitelist;
    } catch (error) {
      if (error instanceof ZemerError) {
        throw error;
      }
      throw new ZemerError(
        'Failed to fetch whitelist from Firestore',
        'NETWORK_ERROR',
        { originalError: error, url: FIRESTORE_WHITELIST_URL }
      );
    }
  }

  async search(query: string): Promise<YouTubeSearchResponse> {
    if (!query.trim()) {
      throw new ZemerError('Search query cannot be empty', 'INVALID_QUERY');
    }

    return this.fetchWithCache<YouTubeSearchResponse>(
      `${this.baseUrl}/search`,
      {
        method: 'POST',
        body: JSON.stringify({ query: query.trim() }),
      },
      `search:${query.toLowerCase().trim()}`
    );
  }

  async browseAlbum(albumId: string): Promise<YouTubeAlbumResponse> {
    if (!albumId) {
      throw new ZemerError('Album ID is required', 'INVALID_ALBUM_ID');
    }

    return this.fetchWithCache<YouTubeAlbumResponse>(
      `${this.baseUrl}/browse/${encodeURIComponent(albumId)}`,
      undefined,
      `album:${albumId}`
    );
  }

  async browseArtist(artistId: string): Promise<any> {
    if (!artistId) {
      throw new ZemerError('Artist ID is required', 'INVALID_ARTIST_ID');
    }

    return this.fetchWithCache<any>(
      `${this.baseUrl}/browse/${encodeURIComponent(artistId)}`,
      undefined,
      `artist:${artistId}`
    );
  }

  clearCache(): void {
    this.requestCache.clear();
  }

  invalidateCache(pattern?: string): void {
    if (!pattern) {
      this.clearCache();
      return;
    }

    const keysToDelete: string[] = [];
    this.requestCache.forEach((_, key) => {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.requestCache.delete(key));
  }
}

// Export singleton instance
export const apiClient = new APIClientImpl();

// Export class for testing
export { APIClientImpl };
